import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import type { ParentStudentLink } from '@tupai/types';
import LinkChildSheet from '@/components/link-child-sheet';
import ConfirmSheet from '@/components/confirm-sheet';
import GateNoticeModal from '@/components/gate-notice-modal';
import ApproveConsentSheet from '@/components/approve-consent-sheet';
import PlanOfferCard from '@/components/plan-offer-card';
import NoChildrenEmptyState from '@/components/no-children-empty-state';
import TrialBanner from '@/components/trial-banner';
import OverLimitBanner from '@/components/over-limit-banner';
import { useCoverageStore } from '@/store/coverage-store';
import { useLinksStore } from '@/store/links-store';
import { useAddChildFlow } from '@/lib/use-add-child-flow';
import { linkHue } from '@/lib/child-identity';
import { IdentityBand, LinkedChildCard } from '@/components/linked-child-card';
import { IconCheck, IconX, IconQrcode, IconPlus } from '@tabler/icons-react';
import { useTranslation } from '@tupai/i18n/react';

export const Route = createFileRoute('/_protected/children/')({
  component: ChildrenPage,
});

function ChildrenPage() {
  const { parentLinks, linkActionId, runLinkAction } = useLinksStore();
  const { t } = useTranslation('children');

  const {
    linkSheetOpen,
    setLinkSheetOpen,
    gateCode,
    setGateCode,
    checkingCapacity,
    openAddChildModal,
  } = useAddChildFlow();
  const [confirmCancel, setConfirmCancel] = useState<ParentStudentLink | null>(null);
  // Accepting is a consent act (TUP-1835), so the consent sheet opens before approving.
  const [approving, setApproving] = useState<ParentStudentLink | null>(null);

  const pendingLinkRequests = parentLinks.filter((l) => l.needs_my_action);
  const linkedChildren = parentLinks.filter((l) => l.status === 'approved');
  const waitingOnChild = parentLinks.filter((l) => l.status === 'pending' && !l.needs_my_action);
  const hasVisibleLinks =
    linkedChildren.length > 0 || pendingLinkRequests.length > 0 || waitingOnChild.length > 0;

  const overLimitHousehold = useCoverageStore((s) => s.household);

  return (
    <div className="flex-1 flex flex-col min-h-0 bg-surface">
      <div className="flex-1 overflow-y-auto px-4 pt-5 pb-4 flex flex-col gap-3">
        <div className="mb-1 flex items-start justify-between gap-2">
          <div>
            <h1 className="font-display text-2xl text-content">{t('title')}</h1>
          </div>
          {hasVisibleLinks && (
            <button
              onClick={() => openAddChildModal()}
              disabled={checkingCapacity}
              aria-label={t('addChild')}
              className="w-11 h-11 rounded-pill bg-primary/10 text-primary flex items-center justify-center shrink-0 cursor-pointer hover:bg-primary/20 transition-colors active:scale-90 disabled:opacity-50"
            >
              <IconPlus size={20} stroke={2.5} />
            </button>
          )}
        </div>
        <TrialBanner />
        <OverLimitBanner household={overLimitHousehold} />
        {!hasVisibleLinks && (
          <NoChildrenEmptyState
            onAddChild={() => openAddChildModal()}
            disabled={checkingCapacity}
          />
        )}

        {/* Status cards share the two-up grid on purpose; don't make them full-width. */}
        <div className="flex flex-col gap-3 md:grid md:grid-cols-2">
          {linkedChildren.map((link) => {
            return (
              <LinkedChildCard
                key={link.link_id}
                link={link}
                hue={linkHue(link.link_id, parentLinks)}
              />
            );
          })}

          {pendingLinkRequests.map((link) => (
            <div
              key={link.link_id}
              // shrink-0: overflow-hidden in a flex column otherwise crushes the card.
              className="bg-surface rounded-lg border border-border overflow-hidden shadow-sm shrink-0"
            >
              <IdentityBand hue={linkHue(link.link_id, parentLinks)} link={link} trailing={null} />
              <div className="p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0 text-sm font-bold text-error bg-error/10 px-3 py-1.5 rounded-md flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-pill bg-error animate-pulse shrink-0" />
                  <span className="truncate">{t('linkRequestPending')}</span>
                </div>
                <button
                  onClick={() => setApproving(link)}
                  disabled={linkActionId === link.link_id}
                  aria-label={t('acceptRequest')}
                  className="w-11 h-11 rounded-pill bg-bg text-green flex items-center justify-center transition-colors cursor-pointer outline-none active:scale-90 disabled:opacity-40 shrink-0"
                >
                  <IconCheck size={16} stroke={3} />
                </button>
                {/* Neutral, not red: red is reserved for the state banner text. */}
                <button
                  onClick={() => runLinkAction(link.link_id, 'reject')}
                  disabled={linkActionId === link.link_id}
                  aria-label={t('declineRequest')}
                  className="w-11 h-11 rounded-pill bg-bg text-muted flex items-center justify-center transition-colors cursor-pointer outline-none active:scale-90 disabled:opacity-40 shrink-0"
                >
                  <IconX size={16} stroke={3} />
                </button>
              </div>
            </div>
          ))}

          {waitingOnChild.map((link) => (
            <div
              key={link.link_id}
              // shrink-0: overflow-hidden in a flex column otherwise crushes the card.
              className="bg-surface rounded-lg border border-border overflow-hidden shadow-sm shrink-0"
            >
              <IdentityBand hue={linkHue(link.link_id, parentLinks)} link={link} trailing={null} />
              <div className="p-4 flex flex-col gap-3">
                <div className="text-sm font-bold text-primary bg-primary/10 px-3 py-1.5 rounded-md flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-pill bg-primary animate-pulse" />
                  <span>
                    {t('waitingForChild', { childName: link.other_party_name || t('yourChild') })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => openAddChildModal()}
                    className="inline-flex items-center gap-1.5 self-start min-h-11 px-3.5 rounded-pill border border-border text-sm font-bold text-primary cursor-pointer"
                  >
                    <IconQrcode size={15} stroke={2} />
                    {t('showCode')}
                  </button>
                  <button
                    onClick={() => setConfirmCancel(link)}
                    disabled={linkActionId === link.link_id}
                    aria-label={t('cancelRequest')}
                    className="w-11 h-11 rounded-pill bg-bg text-muted hover:text-content flex items-center justify-center transition-colors cursor-pointer outline-none active:scale-90 disabled:opacity-40 shrink-0"
                  >
                    <IconX size={16} stroke={3} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <PlanOfferCard />
      </div>

      <LinkChildSheet open={linkSheetOpen} onClose={() => setLinkSheetOpen(false)} />

      <ConfirmSheet
        open={!!confirmCancel}
        onClose={() => setConfirmCancel(null)}
        onConfirm={() => {
          if (confirmCancel) runLinkAction(confirmCancel.link_id, 'revoke');
        }}
        title={t('cancelRequestTitle')}
        body={t('cancelRequestBody', {
          childName: confirmCancel?.other_party_name || t('thisChild'),
        })}
        confirmLabel={t('cancelRequestConfirm')}
      />

      <ApproveConsentSheet
        link={approving}
        onClose={() => setApproving(null)}
        onGate={setGateCode}
      />

      {gateCode && <GateNoticeModal code={gateCode} onClose={() => setGateCode(null)} />}
    </div>
  );
}
